import { useState, useEffect } from 'react';
import { X, Link, Copy, Check, Trash2, MessageSquare, ChevronDown, ChevronUp, Send } from 'lucide-react';
import { T, amountStyle } from '../theme';
import { subscribeOwnerLinks, deleteReviewLink, subscribeComments, addComment } from '../utils/reviewStorage';
import { formatDate, formatAmount } from '../utils/formatters';

const STATUS_META = {
  pending:  { label: 'Pending',  color: 'rgba(255,255,255,0.5)',  bg: 'rgba(255,255,255,0.1)'  },
  returned: { label: 'Returned', color: '#f87171',                bg: 'rgba(248,113,113,0.15)' },
  approved: { label: 'Approved', color: '#34d399',                bg: 'rgba(52,211,153,0.15)'  },
  paid:     { label: 'Paid',     color: T.tealBright,             bg: 'rgba(5,196,176,0.15)'   },
};

function LinkRow({ link, user }) {
  const [expanded, setExpanded] = useState(false);
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState('');
  const [sending, setSending] = useState(false);
  const [copied, setCopied] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!expanded) return;
    const unsub = subscribeComments(link.id, setComments);
    return unsub;
  }, [expanded, link.id]);

  const url = `${window.location.origin}/?review=${link.id}`;
  const statusMeta = STATUS_META[link.status] || STATUS_META.pending;

  const copy = () => {
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const remove = async () => {
    if (!window.confirm('Delete this review link? The reviewer will no longer be able to access it.')) return;
    setDeleting(true);
    await deleteReviewLink(link.id, link.ownerUid, link.fileName).catch(console.error);
  };

  const sendComment = async () => {
    if (!commentText.trim()) return;
    setSending(true);
    try {
      await addComment(link.id, { text: commentText, authorName: user.displayName || user.email || 'Owner', isOwner: true });
      setCommentText('');
    } finally {
      setSending(false);
    }
  };

  const [expenses, setExpenses] = useState([]);
  useEffect(() => {
    if (!expanded || !link.ownerUid || !link.fileName) return;
    import('../utils/reviewStorage').then(m => m.getReviewExpenses(link.ownerUid, link.fileName).then(setExpenses));
  }, [expanded, link.ownerUid, link.fileName]);
  const total = expenses.reduce((s, e) => s + (parseFloat(e.total) || 0), 0);

  return (
    <div style={{ background: 'rgba(255,255,255,0.05)', borderRadius: 12, marginBottom: 10, overflow: 'hidden' }}>
      {/* Header row */}
      <div
        style={{ padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}
        onClick={() => setExpanded(v => !v)}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {link.fileName}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4, flexWrap: 'wrap' }}>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>
              {link.createdAt?.toDate ? formatDate(link.createdAt.toDate().toISOString()) : ''}
            </div>
            {/* <div style={{ fontSize: 12, fontWeight: 700, ...amountStyle(), color: 'rgba(255,255,255,0.6)' }}>
              {formatAmount(total, expenses[0]?.currency || 'USD')}
            </div> */}
            <div style={{ padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 700, color: statusMeta.color, background: statusMeta.bg }}>
              {statusMeta.label}
              {link.reviewerName ? ` · ${link.reviewerName}` : ''}
              {link.reviewerEmail ? ` (${link.reviewerEmail})` : ''}
            </div>
            {link.passwordHash && (
              <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', background: 'rgba(255,255,255,0.08)', padding: '2px 6px', borderRadius: 6 }}>🔒 pw</div>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          {comments.length > 0 && (
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', display: 'flex', alignItems: 'center', gap: 4 }}>
              <MessageSquare size={12} />{comments.length}
            </div>
          )}
          {expanded ? <ChevronUp size={16} color="rgba(255,255,255,0.4)" /> : <ChevronDown size={16} color="rgba(255,255,255,0.4)" />}
        </div>
      </div>

      {/* Expanded panel */}
      {expanded && (
        <div style={{ borderTop: '1px solid rgba(255,255,255,0.08)', padding: '12px 16px' }}>
          {/* Link + actions */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 14, alignItems: 'center' }}>
            <div style={{ flex: 1, fontSize: 11, color: T.tealBright, wordBreak: 'break-all', background: 'rgba(255,255,255,0.05)', borderRadius: 8, padding: '8px 10px' }}>
              {url}
            </div>
            <button onClick={copy} style={{ padding: '8px 12px', borderRadius: 8, border: 'none', background: copied ? T.tealDeep : 'rgba(255,255,255,0.12)', color: '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 5 }}>
              {copied ? <Check size={13} /> : <Copy size={13} />} {copied ? 'Copied' : 'Copy'}
            </button>
            <button onClick={remove} disabled={deleting} style={{ padding: '8px', borderRadius: 8, border: 'none', background: 'rgba(248,113,113,0.12)', color: '#f87171', cursor: 'pointer' }}>
              <Trash2 size={14} />
            </button>
          </div>

          {/* Comments */}
          {comments.length > 0 && (
            <div style={{ marginBottom: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
              {comments.map(c => (
                <div key={c.id} style={{ display: 'flex', gap: 8 }}>
                  <div style={{ flexShrink: 0, width: 28, height: 28, borderRadius: '50%', background: c.isOwner ? T.tealDeep : 'rgba(255,255,255,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: c.isOwner ? T.tealBright : '#fff' }}>
                    {(c.authorName || '?')[0].toUpperCase()}
                  </div>
                  <div style={{ flex: 1, background: 'rgba(255,255,255,0.05)', borderRadius: 8, padding: '8px 10px' }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: c.isOwner ? T.tealBright : 'rgba(255,255,255,0.6)', marginBottom: 3 }}>
                      {c.authorName}{c.isOwner ? ' (Submitter)' : ''}
                      {c.authorEmail && !c.isOwner && (
                        <span style={{ fontWeight: 400, color: 'rgba(255,255,255,0.35)', marginLeft: 6 }}>{c.authorEmail}</span>
                      )}
                    </div>
                    <div style={{ fontSize: 13, color: '#fff' }}>{c.text}</div>
                    {c.createdAt?.toDate && (
                      <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.3)', marginTop: 4 }}>
                        {c.createdAt.toDate().toLocaleString()}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Owner comment input */}
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              placeholder="Reply…"
              value={commentText}
              onChange={e => setCommentText(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && !sending && sendComment()}
              style={{ flex: 1, padding: '9px 12px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.12)', background: 'rgba(255,255,255,0.06)', color: '#fff', fontSize: 13, outline: 'none', fontFamily: T.fontBody }}
            />
            <button
              onClick={sendComment}
              disabled={sending || !commentText.trim()}
              style={{ padding: '9px 12px', borderRadius: 8, border: 'none', background: T.gradTeal, color: '#fff', cursor: sending || !commentText.trim() ? 'not-allowed' : 'pointer', opacity: sending || !commentText.trim() ? 0.5 : 1 }}
            >
              <Send size={14} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function ReviewLinksModal({ user, onClose }) {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsub = subscribeOwnerLinks(user.uid, data => {
      setLinks(data);
      setLoading(false);
    });
    return unsub;
  }, [user.uid]);

  const overlay = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: 20, overflowY: 'auto' };

  return (
    <div style={overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: T.navy, borderRadius: 16, padding: 24, width: '100%', maxWidth: 540, color: '#fff', marginTop: 40 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ background: T.tealDeep, borderRadius: 10, padding: 8 }}><Link size={18} color={T.tealBright} /></div>
            <div style={{ fontSize: 17, fontWeight: 700 }}>Expense Report Review Links</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.5)' }}>
            <X size={18} />
          </button>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 40, color: 'rgba(255,255,255,0.4)', fontSize: 14 }}>Loading…</div>
        ) : links.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, color: 'rgba(255,255,255,0.4)' }}>
            <Link size={32} style={{ margin: '0 auto 12px', opacity: 0.3 }} />
            <div style={{ fontSize: 14 }}>No expense report review links yet.</div>
            <div style={{ fontSize: 12, marginTop: 6 }}>Use "Share for Review" from a report to create one.</div>
          </div>
        ) : (
          links.map(link => <LinkRow key={link.id} link={link} user={user} />)
        )}
      </div>
    </div>
  );
}
